import { useEffect, useState } from "react";
import { Button, Card, Empty, message, Modal, notification, Result, Tabs } from "antd";
import { CloudSyncOutlined, CloudUploadOutlined } from "@ant-design/icons";
import apiClient from "../../../Utils/apiClient";
import PanelLayout from "../../../Layout/PanelLayout";
import PageHeader from "../../../components/PageHeader/PageHeader";
import TableBox from "../../../components/TableBox/TableBox";
import { useHeaderContext } from "../../../Utils/HeaderContext";
import CheckForUpdates from "../CheckforUpdates/CheckForUpdates";
import { pendingWindowsUpdates, hasPendingWindowsUpdates } from "../../../Utils/updateCounts";
import { deviceOs, isUnixDevice, osUpdateLabel } from "../../../Utils/deviceOs";
import { renderDate } from "../../../Utils/utils";

const hotfixInstalledOn = (installed) => {
  if (!installed) return null;
  if (typeof installed === "string" || installed instanceof Date) return installed;
  const dateTime = String(installed.DateTime || "").trim();
  if (dateTime) return dateTime;
  const value = String(installed.value || "").trim();
  return value || null;
};

const { TabPane } = Tabs;

const PatchManagement = ({ panel }) => {
  const { setTaskId } = useHeaderContext();
  const { setRender } = useHeaderContext();
  const [loading, setLoading] = useState(true);
  const [devices, setDevices] = useState({});
  const [click, setClick] = useState(0);
  const currentUrl = window.location.href;
  const userId = currentUrl.split("/").pop();
  const [api, contextHolder] = notification.useNotification();
  const [paginationData, setPaginationData] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
  });
  const [activeTab, setActiveTab] = useState("availablePatches");

  const handleTabChange = (key) => {
    setActiveTab(key);
  };

  const fetchDeviceData = async () => {
    setLoading(true);
    try {
      const data = await apiClient(`devices/${userId}`);
      setDevices({ ...data });
    } catch (error) {
      console.error("Error fetching devices:", error);
      setDevices({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeviceData();
  }, []);

  const handleUnixUpdate = async () => {
    if (!devices?.hostname) return;
    try {
      const response = await apiClient(
        `devices/apply-os-updates/${devices.hostname}`,
        "POST"
      );
      message.success(
        response?.message || `Queued OS updates for ${devices.hostname}`
      );
      if (response?.taskId) {
        setTaskId(response.taskId);
        setRender((pre) => !pre);
      }
    } catch (error) {
      console.error("Error updating OS patches:", error);
      api.error({
        message: "Update Failed",
        description: error?.message || "Could not queue OS updates.",
        duration: 3,
        pauseOnHover: false,
      });
    }
  };

  const handleUpdate = async () => {
    console.log("PatchManagement.js handleUpdate function");

    const patch = pendingWindowsUpdates(devices.windowsUpdates);

    // const data={
    //   kb:patch.kb,
    //   description:patch.description,
    //   releaseDate:patch.releaseDate,
    //   title:patch.title
    // }

    const payload = {
      entityType: `upgradewindows`,
      hostname: devices.hostname,
      command: `windows-update-all`,
      appData: pendingWindowsUpdates(devices.windowsUpdates),
    };

    try {
      const response = await apiClient(
        `devices/${devices.hostname}/create-task`,
        "POST",
        payload
      );
      message.success(`Created Task For ${devices.hostname} ...`);
      if (response) {
        setTaskId(response.taskId);
        setRender((pre) => !pre);
      }
    } catch (error) {
      console.error("Error updating windows patches:", error);
      api.error({
        message: "Update Failed",
        description: "Error updating windows patches.",
        duration: 3,
        pauseOnHover: false,
      });
    }
  };

  const uninstallKb = (row) => {
    const kb = String(row?.HotFixID || "").replace(/^[Kk][Bb]/, "");
    if (!kb || !devices?.hostname) return;
    Modal.confirm({
      title: `Uninstall KB${kb}?`,
      content: `This removes the Windows update from ${devices.hostname}. The PC may need a restart.`,
      okText: "Uninstall",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const response = await apiClient(
            `devices/${devices.hostname}/create-task`,
            "POST",
            {
              entityType: "uninstallwindows",
              hostname: devices.hostname,
              name: `KB${kb}`,
              command: `windows-update-uninstall:${kb}`,
              appData: [row],
            }
          );
          message.success(`Uninstall queued for KB${kb}`);
          if (response?.taskId) {
            setTaskId(response.taskId);
            setRender((pre) => !pre);
          }
        } catch (error) {
          message.error(error?.message || "Could not queue the uninstall");
        }
      },
    });
  };

  const availableColumns = [
    {
      title: "Title",
      dataIndex: "Title",
      key: "Title",
      width: 450,
      render: (text) => text || "N/A",
    },
    {
      title: "KB",
      dataIndex: "KB",
      key: "KB",
      render: (text) => text || "N/A",
    },

    {
      title: "Category",
      dataIndex: "Category",
      key: "Category",
      render: (text) => text || "N/A",
    },
    {
      title: "Version",
      dataIndex: "Version",
      key: "Version",
      render: (text) => text || "N/A",
    },
    {
      title: "Release Date",
      dataIndex: "ReleaseDate",
      key: "ReleaseDate",
      render: (text) => text || "N/A",
    },
  ];

  const appliedColumns = [
    {
      title: "HotFix ID",
      dataIndex: "HotFixID",
      key: "HotFixID",
    },
    {
      title: "Description",
      dataIndex: "Description",
      key: "Description",
    },
    {
      title: "Installed",
      dataIndex: "InstalledOn",
      key: "InstalledOn",
      width: 140,
      render: (value) => {
        const when = hotfixInstalledOn(value);
        return when ? renderDate(when, "DD MMM YYYY") : "—";
      },
    },
    {
      title: "",
      key: "actions",
      width: 110,
      render: (_, row) => (
        <Button size="small" danger onClick={() => uninstallKb(row)}>
          Uninstall
        </Button>
      ),
    },
  ];

  const unixDevice = isUnixDevice(devices);
  const osName = deviceOs(devices) === "macos" ? "macOS" : "Linux";
  const unixHint =
    deviceOs(devices) === "macos"
      ? "This installs available Apple software updates on the device."
      : "This runs apt, dnf, or yum upgrade on the device.";
  const deviceReady = Boolean(devices?._id);

  const content = !deviceReady ? (
    <>
      {contextHolder}
      <Card>
        <Empty description={loading ? "Loading patches…" : "Device not found"} />
      </Card>
    </>
  ) : unixDevice ? (
    <>
      {contextHolder}
      <Card>
        <Result
          status="info"
          title={osUpdateLabel(devices)}
          subTitle={unixHint}
          extra={
            <Button
              type="primary"
              icon={<CloudUploadOutlined />}
              onClick={handleUnixUpdate}
              disabled={!devices?.hostname}
            >
              Update {osName}
            </Button>
          }
        />
      </Card>
    </>
  ) : (
    <>
      {contextHolder}
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        defaultActiveKey="availablePatches"
        className="all-devices-tab"
        tabBarExtraContent={
          <div style={{ display: "flex", gap: 8 }}>
            {hasPendingWindowsUpdates(devices?.windowsUpdates) && (
              <Button
                type="primary"
                size="small"
                icon={<CloudUploadOutlined />}
                onClick={handleUpdate}
              >
                Update All Patches
              </Button>
            )}

            {activeTab === "checkforupdates" && (
              <Button
                type="primary"
                icon={<CloudSyncOutlined />}
                size="small"
                onClick={() => setClick((prev) => prev + 1)}
              >
                Check Updates
              </Button>
            )}
          </div>
        }
      >
        <TabPane tab="Available Windows Updates" key="availablePatches">
          <div className="d-flex flex-column justify-content-between gap-2">
            {pendingWindowsUpdates(devices?.windowsUpdates).length ? (
              <TableBox
                data={pendingWindowsUpdates(devices.windowsUpdates)}
                columns={availableColumns}
                loading={loading}
                pagination={{
                  total: paginationData.total,
                  current: paginationData.page,
                  pageSize: paginationData.pageSize,
                  showSizeChanger: true,
                  pageSizeOptions: ["5", "10", "20", "50"],
                  onChange: (page, pageSize) => {
                    setPaginationData((prev) => ({ ...prev, page, pageSize }));
                  },
                }}
              />
            ) : (
              <Card>
                <Result
                  status="success"
                  title="Windows Updates are up to date"
                />
              </Card>
            )}
          </div>
        </TabPane>

        <TabPane tab="Last Applied Windows Patches" key="lastAppliedPatches">
          {devices?.lastAppliedWindowsPatches?.length ? (
            <TableBox
              data={devices.lastAppliedWindowsPatches}
              columns={appliedColumns}
              loading={loading}
              pagination={{
                total: devices.lastAppliedWindowsPatches.length,
                current: paginationData.page,
                pageSize: paginationData.pageSize,
                showSizeChanger: true,
                pageSizeOptions: ["5", "10", "20", "50"],
                onChange: (page, pageSize) => {
                  setPaginationData((prev) => ({ ...prev, page, pageSize }));
                },
              }}
            />
          ) : (
            <Card>
              <Result
                status="success"
                title="Windows Updates are up to date"
              />
            </Card>
          )}
        </TabPane>
        <TabPane tab="Check For Updates" key="checkforupdates">
          <CheckForUpdates
            activeKey={"checkforupdates"}
            device={devices}
            handleClick={click}
          />
        </TabPane>
      </Tabs>
    </>
  );

  return panel ? (
    <PanelLayout
      title="Patch Management"
      subtitle="Available and applied updates for this device."
    >
      {content}
    </PanelLayout>
  ) : (
    <div className="device-tab-panel">
      <PageHeader
        title="Patches"
        subtitle="Scan, install, and review Windows or OS updates for this endpoint."
      />
      {content}
    </div>
  )
}

export default PatchManagement;
